import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { http, formatIDR, formatDate } from "@/lib/api";
import { Printer, CalendarBlank, TrendUp, Trophy, XCircle, Users } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";

const stageOrder = [
  "lead",
  "quotation",
  "discuss_mgmt",
  "negotiation",
  "invoice",
  "payment",
  "pending",
  "lose",
];
const stageColor = {
  lead: "#5A6B76",
  quotation: "#0284C7",
  discuss_mgmt: "#7C3AED",
  negotiation: "#4F46E5",
  invoice: "#D97706",
  payment: "#16A34A",
  pending: "#F59E0B",
  lose: "#DC2626",
};
const stageLabel = {
  lead: "Prospek",
  quotation: "Make an Offer / Quotation",
  discuss_mgmt: "Discuss Management",
  negotiation: "Negotiation / Finalize proposal",
  invoice: "Send Invoice / Waiting Payment",
  payment: "Payment",
  pending: "Pending",
  lose: "Lose",
};

function monthLabel(m) {
  if (!m) return "";
  const [y, mm] = m.split("-").map(Number);
  const names = ["Januari","Februari","Maret","April","Mei","Juni","Juli","Agustus","September","Oktober","November","Desember"];
  return `${names[mm - 1]} ${y}`;
}

function monthOptions() {
  const now = new Date();
  const opts = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    opts.push({ value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: `${["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agu","Sep","Okt","Nov","Des"][d.getMonth()]} ${d.getFullYear()}` });
  }
  return opts;
}

export default function ReportsPage() {
  const options = useMemo(monthOptions, []);
  const [month, setMonth] = useState(options[0].value);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await http.get(`/reports/monthly?month=${month}`);
        setReport(data);
      } finally { setLoading(false); }
    })();
  }, [month]);

  const chartData = report
    ? stageOrder.map((s) => ({ stage: stageLabel[s], key: s, count: report.stage_counts[s], value: report.stage_values[s] }))
    : [];

  const print = () => window.print();

  return (
    <AppLayout>
      {/* SCREEN CONTROLS - hidden when printing */}
      <div className="no-print flex flex-col sm:flex-row sm:items-end justify-between gap-3 sm:gap-4 mb-5 sm:mb-8">
        <div>
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#64748B] font-bold">Reports</div>
          <h1 className="font-display text-2xl sm:text-4xl lg:text-5xl tracking-tight mt-1 text-[#173042]">Laporan Bulanan</h1>
          <p className="text-[#64748B] mt-1 text-xs sm:text-sm">Rekap pipeline & closing untuk direksi. Klik Cetak untuk simpan sebagai PDF.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            data-testid="month-select"
            className="rounded-lg border border-[#CBD5E1] bg-white px-3 py-2 text-xs sm:text-sm font-semibold text-[#173042] focus:outline-none focus:border-[#173B4D]"
          >
            {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button
            onClick={print}
            data-testid="print-report-btn"
            className="bg-[#173B4D] hover:bg-[#112B38] text-white px-4 py-2 rounded-lg flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider shadow-xs transition-colors"
          >
            <Printer size={16} /> <span>Cetak / PDF</span>
          </button>
        </div>
      </div>

      {loading || !report ? (
        <div className="text-xs text-[#64748B] uppercase tracking-[0.15em] font-semibold py-8">Memuat laporan...</div>
      ) : (
        <div className="report-sheet bg-white rounded-xl border border-[#E2E8F0] p-4 sm:p-6 lg:p-10 shadow-2xs" data-testid="report-sheet">
          {/* Report Header */}
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 pb-5 sm:pb-6 border-b border-[#173B4D]">
            <div>
              <div className="flex items-center gap-2.5">
                <img src="/nusacert-logo.png" alt="Nusacert" className="h-7 sm:h-8 object-contain" />
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] font-bold mt-3">Laporan Sales Bulanan</div>
              <h2 className="font-display text-2xl sm:text-4xl tracking-tight mt-1 text-[#173042]">{monthLabel(report.month)}</h2>
            </div>
            <div className="pt-2 sm:pt-0 border-t sm:border-t-0 border-[#E2E8F0] sm:text-right">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] font-bold">Digenerate</div>
              <div className="font-mono-data text-xs text-[#173042] mt-0.5">{new Date(report.generated_at).toLocaleString("id-ID")}</div>
            </div>
          </div>

          {/* Executive Summary */}
          <div className="mt-6 sm:mt-8">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] font-bold">Ringkasan Eksekutif</div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4 mt-3">
              <SummaryTile label="Pipeline Baru" value={`${report.totals.created_count} deal`} sub={formatIDR(report.totals.created_value)} icon={TrendUp} accent="#1C4E63" testid="sum-created" />
              <SummaryTile label="Deal Won" value={`${report.totals.won_count} deal`} sub={formatIDR(report.totals.won_value)} icon={Trophy} accent="#2E8B57" testid="sum-won" />
              <SummaryTile label="Deal Lost" value={`${report.totals.lost_count} deal`} sub={formatIDR(report.totals.lost_value)} icon={XCircle} accent="#D63230" testid="sum-lost" />
              <SummaryTile label="Conversion" value={`${report.totals.conversion_rate}%`} sub={`Won / Deal baru`} icon={Users} accent="#D9A321" testid="sum-conv" />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4 mt-2.5 sm:mt-4">
              <MiniStat label="Pipeline Terbuka" value={formatIDR(report.totals.open_pipeline_value)} />
              <MiniStat label="Kontak Baru" value={report.totals.new_contacts} />
              <MiniStat label="Meeting" value={report.totals.meetings_count} />
              <MiniStat label="Aktivitas" value={report.totals.activities_count} />
            </div>
          </div>

          {/* Distribution chart */}
          <div className="mt-8 sm:mt-10">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] font-bold">Distribusi Pipeline saat ini</div>
            <div className="h-56 sm:h-64 mt-3">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="stage" tick={{ fill: "#64748B", fontSize: 10 }} axisLine={{ stroke: "#E2E8F0" }} interval={0} />
                  <YAxis tick={{ fill: "#64748B", fontSize: 10 }} axisLine={{ stroke: "#E2E8F0" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 11 }} formatter={(v, _, e) => [`${v} deal · ${formatIDR(e.payload.value)}`, e.payload.stage]} />
                  <Bar dataKey="count" isAnimationActive={false} radius={[4, 4, 0, 0]}>
                    {chartData.map((d) => <Cell key={d.key} fill={stageColor[d.key]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Per Owner */}
          <div className="mt-8 sm:mt-10 avoid-break">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] font-bold mb-3">Performa per Sales</div>
            <div className="overflow-x-auto rounded-lg border border-[#E2E8F0]">
              <table className="w-full min-w-[700px]" data-testid="per-owner-table">
                <thead>
                  <tr className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
                    <Th>Anggota</Th>
                    <Th>Peran</Th>
                    <Th className="text-right">Deal Baru</Th>
                    <Th className="text-right">Nilai Baru</Th>
                    <Th className="text-right">Won</Th>
                    <Th className="text-right">Nilai Won</Th>
                    <Th className="text-right">Pipeline Terbuka</Th>
                  </tr>
                </thead>
                <tbody>
                  {report.per_owner.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-6 text-sm text-[#64748B]">Tidak ada data untuk bulan ini.</td></tr>
                  )}
                  {report.per_owner.map((r) => (
                    <tr key={r.owner_id} className="border-b border-[#E2E8F0]">
                      <Td className="font-semibold text-xs text-[#173042]">{r.owner_name}</Td>
                      <Td className="uppercase text-[9px] font-bold tracking-[0.1em] text-[#64748B]">{r.owner_role}</Td>
                      <Td className="text-right font-mono-data text-xs">{r.created_count}</Td>
                      <Td className="text-right font-mono-data text-xs">{formatIDR(r.created_value)}</Td>
                      <Td className="text-right font-mono-data text-xs text-[#2E8B57] font-bold">{r.won_count}</Td>
                      <Td className="text-right font-mono-data text-xs text-[#2E8B57] font-bold">{formatIDR(r.won_value)}</Td>
                      <Td className="text-right font-mono-data text-xs">{formatIDR(r.open_value)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Won Deals */}
          <div className="mt-8 sm:mt-10 avoid-break">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] font-bold mb-3">Deal Won bulan ini</div>
            <div className="overflow-x-auto rounded-lg border border-[#E2E8F0]">
              <table className="w-full min-w-[650px]" data-testid="won-deals-table">
                <thead>
                  <tr className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
                    <Th>Deal</Th>
                    <Th>Perusahaan</Th>
                    <Th>Sales</Th>
                    <Th>Tanggal Close</Th>
                    <Th className="text-right">Nilai</Th>
                  </tr>
                </thead>
                <tbody>
                  {report.won_deals.length === 0 && (
                    <tr><td colSpan={5} className="text-center py-6 text-sm text-[#64748B]">Belum ada deal Won.</td></tr>
                  )}
                  {report.won_deals.map((d, i) => (
                    <tr key={i} className="border-b border-[#E2E8F0]">
                      <Td className="font-semibold text-xs text-[#173042]">{d.title}</Td>
                      <Td className="text-xs text-[#64748B]">{d.company || "-"}</Td>
                      <Td className="text-xs text-[#64748B]">{d.owner_name}</Td>
                      <Td className="font-mono-data text-xs text-[#64748B]">{formatDate(d.closed_at)}</Td>
                      <Td className="text-right font-mono-data text-xs font-bold text-[#2E8B57]">{formatIDR(d.value)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Signature block */}
          <div className="mt-12 sm:mt-16 grid grid-cols-2 gap-8 sm:gap-16 avoid-break">
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Disiapkan oleh</div>
              <div className="mt-12 sm:mt-16 border-t border-[#173042] pt-1 text-xs font-mono-data text-[#64748B]">Sales Operations</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Disetujui oleh</div>
              <div className="mt-12 sm:mt-16 border-t border-[#173B4D] pt-1 text-xs font-mono-data text-[#64748B]">Direksi</div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}

function SummaryTile({ label, value, sub, icon: Icon, accent, testid }) {
  return (
    <div className="rounded-xl border border-[#E2E8F0] border-t-4 p-3.5 sm:p-4 bg-white shadow-2xs" style={{ borderTopColor: accent }} data-testid={testid}>
      <div className="flex items-center justify-between gap-1">
        <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.12em] font-bold text-[#64748B] truncate">{label}</div>
        <Icon size={16} className="text-[#64748B] shrink-0" />
      </div>
      <div className="font-mono-data text-base sm:text-xl font-bold text-[#173042] mt-1.5 truncate">{value}</div>
      <div className="font-mono-data text-[10px] sm:text-xs text-[#64748B] mt-0.5 truncate">{sub}</div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-xl border border-[#E2E8F0] p-3 sm:p-4 bg-[#F8FAFC]">
      <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.12em] font-bold text-[#64748B] truncate">{label}</div>
      <div className="font-mono-data text-sm sm:text-lg font-bold text-[#173042] mt-1 truncate">{value}</div>
    </div>
  );
}

const Th = ({ children, className = "" }) => <th className={`py-3 px-3.5 font-bold uppercase tracking-wider text-[10px] text-[#64748B] text-left ${className}`}>{children}</th>;
const Td = ({ children, className = "" }) => <td className={`py-3 px-3.5 text-xs text-[#173042] ${className}`}>{children}</td>;
